import CheckIcon from "@mui/icons-material/Check";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid2,
  Paper,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { PimoReactComponent } from "@pimo/pimo-app-builder";
import { usePimoForm } from "@pimo/pimo-components/src/lib/pimo-components/use-pimo-form/use-pimo-form.hook";
import { getLocalizedMessage } from "@pimo/pimo-l10n";
import React from "react";
import {
  Configuration,
  ExtendedFieldError,
  FieldDefinition,
  PROJECT_NAMES,
} from "vmd-types";

import { SUCCESS_COLORS, TOGGLE_GREEN } from "../../constants/colors";
import { AntSwitch } from "../components/ant-switch";
import { CategoryFields } from "../custom-fields/category-field";
import { DefaultFields } from "../custom-fields/default-field";

export interface ProjectsBasicFieldsFormProps {
  configuration?: Configuration;
  language?: string;
  setUpdatedFieldsFlag: (change: "FieldActivated" | "FieldDeactivated") => void;
}

export const ProjectsBasicFieldsForm: PimoReactComponent<
  ProjectsBasicFieldsFormProps,
  "define:project-data",
  Configuration
> = ({ setUpdatedFieldsFlag, language = "en" }) => {
  const { setValue, formState } = usePimoForm<{
    configuration: Configuration;
    validationErrors: Record<string, ExtendedFieldError>;
    enableSaveButton: boolean;
  }>({
    appStateSlice: "editSettingsForm",
  });
  const projectName = PROJECT_NAMES.find(
    (entry) =>
      entry.single === (formState.configuration?.projectName ?? "Project")
  );

  const [isAccordionExpanded, setIsAccordionExpanded] = React.useState<
    string | false
  >(false);

  const handleChange =
    (panel: string) => (_: React.SyntheticEvent, newExpanded: boolean) => {
      if (panel === "category" || panel === "secondCategory") {
        setIsAccordionExpanded(newExpanded ? panel : false);
      }
    };
  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        height: "calc(100vh - 83px)",
        overflowY: "auto",
      }}
    >
      <Typography
        sx={{
          fontSize: 24,
          fontWeight: 500,
        }}
      >
        {getLocalizedMessage(language, "dataModel.masterDataProject") ||
          "Master Data"}{" "}
        {projectName?.plural}
      </Typography>
      <Typography
        sx={{
          fontSize: "14px",
          color: grey[600],
        }}
      >
        {projectName?.single}{" "}
        {getLocalizedMessage(
          language,
          "dataModel.projectMasterDataDescription"
        ) ||
          `master data is the data that is entered when a new ${projectName?.single} is created. Please select the data fields.`}
      </Typography>
      <Paper
        sx={{
          borderRadius: "12px",
          boxShadow: "none",
          border: "1px solid",
          borderColor: grey[300],
        }}
      >
        <Grid2
          size={12}
          container
          alignItems="center"
          sx={{ height: 65, p: 2 }}
        >
          <Grid2 size={3} sx={{ fontSize: "14px", fontWeight: 500 }}>
            {getLocalizedMessage(language, "dataField") || "Data Field"}
          </Grid2>
          <Grid2 size={3} sx={{ fontSize: "14px", fontWeight: 500 }}>
            {getLocalizedMessage(language, "dataType") || "Data Type"}
          </Grid2>
          <Grid2 size={3} sx={{ fontSize: "14px", fontWeight: 500 }}></Grid2>
          <Grid2 size={3} sx={{ fontSize: "14px", fontWeight: 500 }}></Grid2>
        </Grid2>
        {formState.configuration?.fieldsDefinition
          ?.filter((f) => f.purpose === "projectBasic")
          .map((field) => {
            return (
              <Accordion
                expanded={isAccordionExpanded === field.fieldName}
                onChange={handleChange(field.fieldName)}
                sx={{
                  borderRadius: "12px",
                }}
                elevation={0}
                key={field.fieldName + field.purpose}
              >
                <AccordionSummary
                  aria-controls="panel1-content"
                  id="panel1-header"
                  expandIcon={
                    field.type === "Dropdown" ? <ExpandMoreIcon /> : null
                  }
                  sx={{
                    cursor: "default",
                    userSelect: "text",
                    width: field.type === "Dropdown" ? "100%" : "98%",
                    height: 65,
                  }}
                >
                  <Grid2
                    size={12}
                    container
                    alignItems="center"
                    key={field.fieldName + field.purpose + "grid"}
                    sx={{ height: 65, borderRadius: "12px" }}
                  >
                    <Grid2
                      size={3}
                      sx={{ fontSize: "14px", borderRadius: "12px" }}
                    >
                      {field.fieldName === "approvedBudget"
                        ? `${field.label === "Budget" ? "Approved Budget" : field.label} (€)`
                        : field.label}
                    </Grid2>
                    <Grid2 size={3} sx={{ fontSize: "14px" }}>
                      {field.type}
                    </Grid2>
                    <Grid2 size={3}>
                      <FormControlLabel
                        labelPlacement="start"
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "14px",
                          },
                        }}
                        control={
                          <Checkbox
                            onClick={(e) => {
                              // stop the click from bubbling to AccordionSummary
                              e.stopPropagation();
                            }}
                            onChange={(e) => {
                              const currentField = {
                                ...field,
                                isMandatory: e.target.checked,
                              };

                              const newFieldsDefinition =
                                formState.configuration?.fieldsDefinition ?? [];

                              const fullArrayIndex =
                                newFieldsDefinition.findIndex(
                                  (f) =>
                                    f.fieldName === field.fieldName &&
                                    f.purpose === field.purpose
                                );

                              if (fullArrayIndex !== -1) {
                                newFieldsDefinition[fullArrayIndex] =
                                  currentField;
                                setValue(
                                  `configuration.fieldsDefinition`,
                                  newFieldsDefinition
                                );
                              }
                              if (formState.enableSaveButton !== true) {
                                setValue("enableSaveButton", true);
                              }
                            }}
                            disabled={!field.isEditable}
                            icon={
                              <span
                                style={{
                                  width: 20,
                                  height: 20,
                                  border: "2px solid",
                                  borderColor: !field.isEditable
                                    ? grey[400]
                                    : grey[700],

                                  display: "inline-block",
                                  backgroundColor: "#fff",
                                }}
                              />
                            }
                            checkedIcon={
                              <span
                                style={{
                                  width: 20,
                                  height: 20,

                                  border: "2px solid",
                                  borderColor: !field.isEditable
                                    ? grey[400]
                                    : grey[700],
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "#fff",
                                }}
                              >
                                <CheckIcon
                                  style={{
                                    fontSize: 18,
                                    color: !field.isEditable
                                      ? grey[400]
                                      : grey[700],
                                  }}
                                />
                              </span>
                            }
                            checked={field.isMandatory}
                          />
                        }
                        label={
                          getLocalizedMessage(language, "mandatory") ||
                          "Mandatory"
                        }
                      />
                    </Grid2>
                    <Grid2 size={3} sx={{ fontSize: "14px" }}>
                      {!field.isEditable ? (
                        <Chip
                          label={
                            getLocalizedMessage(
                              language,
                              "constantlyActiveField"
                            ) || "Constantly Active Field"
                          }
                          sx={{
                            color: SUCCESS_COLORS.text,
                            fontWeight: 500,
                            backgroundColor: SUCCESS_COLORS.background,
                          }}
                        ></Chip>
                      ) : (
                        <FormControlLabel
                          control={
                            <AntSwitch
                              checked={field.enabled}
                              onClick={(e) => {
                                e.stopPropagation();
                              }}
                              onChange={(e) => {
                                e.stopPropagation();
                                const currentField = {
                                  ...field,
                                  enabled: e.target.checked,
                                };

                                const newFieldsDefinition = [
                                  ...(formState.configuration
                                    ?.fieldsDefinition ?? []),
                                ];
                                // Find the correct index in the full array
                                const fullArrayIndex =
                                  newFieldsDefinition.findIndex(
                                    (f) =>
                                      f.fieldName === field.fieldName &&
                                      f.purpose === field.purpose
                                  );

                                if (fullArrayIndex !== -1) {
                                  newFieldsDefinition[fullArrayIndex] =
                                    currentField;
                                  setValue(
                                    `configuration.fieldsDefinition`,
                                    newFieldsDefinition
                                  );
                                }
                                setValue("enableSaveButton", true);
                                setUpdatedFieldsFlag(
                                  e.target.checked
                                    ? "FieldActivated"
                                    : "FieldDeactivated"
                                );
                              }}
                            />
                          }
                          label={
                            getLocalizedMessage(language, "activate") ||
                            "Activate"
                          }
                          labelPlacement="start"
                          sx={{
                            "& .MuiSwitch-thumb": {
                              backgroundColor: grey[300],
                            },
                            "& .MuiSwitch-track": {
                              color: TOGGLE_GREEN,
                            },
                            "& .MuiFormControlLabel-label": {
                              fontSize: "14px",
                            },
                          }}
                        />
                      )}
                    </Grid2>
                  </Grid2>
                </AccordionSummary>
                {renderTheCorrectComponent(
                  setValue,
                  formState.configuration,
                  formState.validationErrors,
                  field,
                  formState.enableSaveButton
                )}
              </Accordion>
            );
          })}
      </Paper>
    </Box>
  );
};

const renderTheCorrectComponent = (
  setValue: (key: string, value: unknown) => void,
  configuration: Configuration | undefined,
  validationErrors: Record<string, ExtendedFieldError>,
  field: FieldDefinition & {
    purpose:
      "projectReporting" | "itemReporting" | "projectBasic" | "itemBasic";
  },
  enableSaveButton?: boolean
) => {
  switch (field.fieldInConfiguration) {
    case "categoryDefinition":
      return (
        <AccordionDetails>
          <CategoryFields
            baseFieldName="categoryDefinition"
            initialEntries={configuration?.categoryDefinition ?? []}
            onEntriesChange={(val) => {
              setValue(`configuration.categoryDefinition`, val);

              const newErrors: Record<string, ExtendedFieldError> = {
                ...(validationErrors ?? {}),
              };

              Object.keys(newErrors).forEach((k) => {
                if (k.startsWith("categoryDefinition.")) delete newErrors[k];
              });

              val.forEach((item, index) => {
                if ((item.name ?? "").trim() === "") {
                  newErrors[`categoryDefinition.${index}.name`] = {
                    type: "required",
                    message: "Status name cannot be empty",
                    tabContainingErrors: `Master Data ${configuration?.projectName}`,
                  } as ExtendedFieldError;
                }
              });

              setValue("validationErrors", newErrors);
              if (enableSaveButton !== true) {
                setValue("enableSaveButton", true);
              }
            }}
            fieldName={field.label}
            onFieldNameChange={(name) => {
              const indexOfField =
                configuration?.fieldsDefinition?.findIndex(
                  (f) =>
                    f.fieldName === field.fieldName &&
                    f.purpose === field.purpose
                ) ?? 0;
              const newFieldsDefinition = configuration?.fieldsDefinition ?? [];
              newFieldsDefinition[indexOfField] = {
                ...newFieldsDefinition[indexOfField],
                label: name,
              };
              if (name.trim() === "") {
                const newErrors: Record<string, ExtendedFieldError> = {
                  ...(validationErrors ?? {}),
                };
                newErrors[`categoryDefinition`] = {
                  type: "required",
                  message: "Field label cannot be empty",
                  tabContainingErrors: `Master Data ${configuration?.projectName}`,
                } as ExtendedFieldError;
                setValue("validationErrors", newErrors);
              } else {
                const newErrors = Object.keys(validationErrors ?? {}).reduce(
                  (acc, key) => {
                    if (key !== `${field.fieldName}`) {
                      acc[key] = validationErrors[key];
                    }
                    return acc;
                  },
                  {} as Record<string, ExtendedFieldError>
                );
                setValue("validationErrors", newErrors);
              }
              setValue(`configuration.fieldsDefinition`, newFieldsDefinition);

              if (enableSaveButton !== true) {
                setValue("enableSaveButton", true);
              }
            }}
            validationErrors={validationErrors}
          />
        </AccordionDetails>
      );

    case "secondCategoryDefinition":
      return (
        <AccordionDetails>
          <CategoryFields
            baseFieldName="secondCategoryDefinition"
            initialEntries={configuration?.secondCategoryDefinition ?? []}
            onEntriesChange={(val) => {
              setValue(`configuration.secondCategoryDefinition`, val);

              const newErrors: Record<string, ExtendedFieldError> = {
                ...(validationErrors ?? {}),
              };

              Object.keys(newErrors).forEach((k) => {
                if (k.startsWith("secondCategoryDefinition."))
                  delete newErrors[k];
              });

              val.forEach((item, index) => {
                if ((item.name ?? "").trim() === "") {
                  newErrors[`secondCategoryDefinition.${index}.name`] = {
                    type: "required",
                    message: "Status name cannot be empty",
                    tabContainingErrors: `Master Data ${configuration?.projectName}`,
                  } as ExtendedFieldError;
                }
              });

              setValue("validationErrors", newErrors);
              if (enableSaveButton !== true) {
                setValue("enableSaveButton", true);
              }
            }}
            fieldName={field.label}
            onFieldNameChange={(name) => {
              const index =
                configuration?.fieldsDefinition?.findIndex(
                  (f) =>
                    f.fieldName === field.fieldName &&
                    f.purpose === field.purpose
                ) ?? 0;
              const newFieldsDefinition = configuration?.fieldsDefinition ?? [];
              newFieldsDefinition[index] = {
                ...newFieldsDefinition[index],
                label: name,
              };
              setValue(`configuration.fieldsDefinition`, newFieldsDefinition);
              if (enableSaveButton !== true) {
                setValue("enableSaveButton", true);
              }
            }}
            validationErrors={validationErrors}
          />
        </AccordionDetails>
      );
    default:
      return (
        <AccordionDetails>
          <DefaultFields />
        </AccordionDetails>
      );
  }
};
